/**
 * useResilientLoad — load multiple API calls in parallel without letting any
 * one failure unmount the page.
 *
 * Background: Pages frequently call `Promise.all([primary, secondary1, secondary2])`,
 * which rejects the whole load if ANY call fails. When a user has access to the
 * primary module (e.g., fleet) but not to a secondary lookup (e.g., locations),
 * the secondary 403 blanks the whole page.
 *
 * This helper:
 *   - Runs all loaders via `Promise.allSettled`
 *   - Returns each call's data on success, OR a `{ denied: true }` marker on 403
 *   - Logs other errors (5xx, network) as `{ error: ... }` so caller can surface
 *
 * Usage:
 *   const { results, loading } = useResilientLoad(useMemo(() => ({
 *     vehicles: () => api.get('/fleet/vehicles'),
 *     locations: () => lookupsAPI.locations(),
 *   }), []));
 *
 *   results.vehicles.data   // [] or actual data
 *   results.locations.denied // true if the call 403'd
 */
import { useState, useEffect } from 'react';

export function useResilientLoad(loaders, deps = []) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const keys = Object.keys(loaders);
    setLoading(true);
    Promise.allSettled(keys.map(k => loaders[k]()))
      .then(settled => {
        if (cancelled) return;
        const out = {};
        keys.forEach((k, i) => {
          const s = settled[i];
          if (s.status === 'fulfilled') {
            out[k] = { data: s.value?.data ?? s.value, ok: true };
          } else {
            const status = s.reason?.response?.status;
            const denied = status === 403;
            out[k] = {
              data: null,
              ok: false,
              denied,
              status,
              error: s.reason?.response?.data?.detail || s.reason?.message || 'Request failed',
            };
            if (!denied) {
              // 403 is an expected degradation; everything else is genuinely broken.
              // eslint-disable-next-line no-console
              console.error(`useResilientLoad[${k}] failed:`, s.reason);
            }
          }
        });
        setResults(out);
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { results, loading };
}

/**
 * settle — drop-in resilient replacement for Promise.all.
 * Returns an array of `{ data, ok, denied, status, error }` in the same order.
 * Useful for one-off loaders that don't want the hook ceremony.
 */
export async function settle(promises) {
  const out = await Promise.allSettled(promises);
  return out.map(s => {
    if (s.status === 'fulfilled') {
      return { data: s.value?.data ?? s.value, ok: true };
    }
    const status = s.reason?.response?.status;
    return {
      data: null,
      ok: false,
      denied: status === 403,
      status,
      error: s.reason?.response?.data?.detail || s.reason?.message || 'Request failed',
    };
  });
}
